import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import {
    fetchConditionCodes,
    fetchModifiers,
    fetchOccurrenceCodes,
    fetchPlaceOfService,
    fetchProcedureCodes,
    fetchServices,
    fetchTypeOfBill,
} from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";

const limit = 20;

const SERVICE_COLUMNS = [
    "serviceCode",
    "standardizedServiceCode",
    "serviceTypeName",
    "serviceShortDesc",
    "serviceLongDesc",
];

export const CODE_CATALOGS = {
    revenue: {
        title: "Revenue Codes",
        subtitle: "UB-04 revenue codes used on institutional claim lines",
        fetch: (params) => fetchServices({ ...params, serviceTypeName: "UB Revenue Code" }),
        columns: SERVICE_COLUMNS,
        type: "service-code",
    },
    drugs: {
        title: "Drug Codes",
        subtitle: "NDC (National Drug Code) used on pharmacy and medical drug lines",
        fetch: (params) => fetchServices({ ...params, serviceTypeName: "NDC 5-4-2" }),
        columns: SERVICE_COLUMNS,
        type: "service-code",
    },
    "place-of-service": {
        title: "Place of Service",
        subtitle: "CMS place of service codes used on professional claim lines (e.g. 11 office, 21 inpatient, 23 ER)",
        fetch: fetchPlaceOfService,
        columns: ["placeOfServiceCode", "placeOfServiceDesc", "isFacilityPaymentSite", "admitStatusCode"],
        type: "place-of-service",
    },
    "type-of-bill": {
        title: "Type of Bill",
        subtitle: "UB-04 type of bill codes used on institutional claims (e.g. 111 inpatient, 131 outpatient)",
        fetch: fetchTypeOfBill,
        columns: ["typeOfBillCode", "typeOfBillDesc", "admitStatusCode"],
        type: "type-of-bill",
    },
    modifiers: {
        title: "Modifiers",
        subtitle: "CPT / HCPCS modifiers used on claim lines (e.g. 25, 50, TC)",
        fetch: fetchModifiers,
        columns: ["modifierCode", "modifierName", "modifierDesc"],
        type: "modifiers",
    },
    procedures: {
        title: "Procedure Codes",
        subtitle: "ICD procedure codes on institutional claims — not CPT / HCPCS",
        fetch: fetchProcedureCodes,
        columns: ["procedureCode", "standardizedProcedureCode", "procedureTypeName", "procedureShortDesc", "procedureLongDesc"],
        type: "procedure-code",
    },
    condition: {
        title: "Condition Codes",
        subtitle: "UB-04 condition codes attached to the institutional claim header",
        fetch: fetchConditionCodes,
        columns: ["conditionCode", "conditionCodeDesc"],
        type: "condition-code",
    },
    occurrence: {
        title: "Occurrence Codes",
        subtitle: "UB-04 occurrence codes and spans on the institutional claim header",
        fetch: fetchOccurrenceCodes,
        columns: ["occurrenceCode", "occurrenceCodeDesc"],
        type: "occurrence-code",
    },
};

const CodeCatalog = ({ catalogId }) => {
    const catalog = CODE_CATALOGS[catalogId];
    const [searchValue, setSearchValue] = useState("");
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();
    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: ["code-catalog", catalogId, limit, decouncedSearchValue],
        queryFn: async ({ pageParam }) => {
            const params = { ...(pageParam || {}), limit };
            if (decouncedSearchValue) params.searchText = decouncedSearchValue;
            return await catalog.fetch(params);
        },
        select: (result) => ({
            count: result.pages?.[0]?.count,
            rows: result.pages.map((page) => page.rows).flatMap((x) => x),
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            if (lastPage?.rows?.length !== limit) return undefined;
            return {
                offset: pages.reduce((accum, page) => accum + page.rows.length, 0),
            };
        },
        staleTime: 0,
        enabled: !!catalog,
    });

    useEffect(() => {
        if (inView) fetchNextPage();
    }, [inView, fetchNextPage]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        [],
    );

    if (!catalog) {
        return (
            <div className="container">
                <p className="title">Unknown code catalog</p>
            </div>
        );
    }

    return (
        <div className="container">
            <p className="title">{catalog.title}  ( {data?.count || 0} )</p>
            <p className="sub-title">{catalog.subtitle}</p>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1rem 1rem 0rem" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", width: "100%", flexWrap: "wrap" }}>
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={`search-${catalogId}`}
                        placeholder="search..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setDecouncedSearchValue("");
                        setSearchValue("");
                    }}
                >
                    Clear filters
                </p>
            </div>

            {status === "loading" || status === "pending" || isLoading ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className="danger">Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={catalog.type}
                    columns={catalog.columns}
                    items={data?.rows}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    searchText={decouncedSearchValue}
                    ref={ref}
                />
            )}
        </div>
    );
};

export default CodeCatalog;
